import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {ProvisionAgentConfigurationDto} from '../../model/dto/api/provision-agent-configuration.dto';

/**
 * Custom API service for the RESOLVED provision configuration of ONE Abrechnungskonto
 * (`cp.ProvisionAgent`) — the data behind the navItem "Provisionskonfiguration".
 *
 * Base URL `/mvsa/cp/provisionAgents`, the route the alias `cp.ProvisionAgent` already carries in
 * cp.entity-provider.ts. Shape follows the sibling read service of this module,
 * {@link CpProvisionAgentCoverageService}: an `MvsCrudService` subclass whose only job is the custom
 * endpoints of that route, while the generic CRUD of the alias keeps going through the registry
 * service.
 *
 * READ-ONLY, and it writes nothing on the server either — the endpoint is `@Transactional(readOnly
 * = true)` and only reads the configuration graph.
 *
 * WHY AN ENDPOINT AND NOT QL (capability-matrix row F1, JUSTIFIED-CUSTOM): resolving one displayed
 * row means walking the K-7 `parentMatrix` chain — up to four levels deep — and applying the BR-050
 * rank between the agent leg and the group leg. QL has no recursion, and every alternative examined
 * in the design gate (`rp.RpLogicReport` with a Groovy merge step, an `xr` cross report, a frontend
 * QL assembly) does not avoid that walk, it merely MOVES it into a second language beside
 * `ProvisionTypeRuntime`. The signed wording: "jede Alternative verlagert dieselbe Rekursion in
 * eine zweite Sprache."
 */
@Injectable({
    providedIn: 'root'
})
export class CpProvisionAgentConfigurationService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cp/provisionAgents');
    }

    /**
     * GET /{id}/provisionConfiguration — the resolved configuration of one Abrechnungskonto on one
     * key date.
     *
     * The response is a STATE, never an empty page: `NO_GROUP_AT_KEY_DATE` arrives together with
     * the memberships the agent holds in other Provisionsarten, so the caller can explain the empty
     * answer instead of showing nothing. Callers must therefore branch on
     * `ProvisionAgentConfigurationDto.state` and must NOT treat an empty `components` array as an
     * error.
     *
     * Every optional parameter is OMITTED rather than sent empty when it is not set — the server
     * defaults `keyDate` to today and derives `provisionTypeId` from the agent's memberships, and
     * sending an empty string instead would be a 400.
     *
     * @param provisionAgentId       id of the Abrechnungskonto (`cp.ProvisionAgent`)
     * @param provisionTypeId        optional Provisionsart; derived by the server when omitted
     * @param keyDate                optional key date as `YYYY-MM-DD`; today when omitted. This is
     *                               the CONTRACT START date, not the run date (requirement §7.1)
     * @param includeGroupComparison whether the rows of the other Vermittlergruppen come along;
     *                               default false, and guarded on the server by a second
     *                               `checkAccess` on the Provisionsart (FD-5)
     */
    public getProvisionConfiguration(
        provisionAgentId: number,
        provisionTypeId?: number,
        keyDate?: string,
        includeGroupComparison?: boolean): Observable<ProvisionAgentConfigurationDto> {

        let params = new HttpParams();

        if (provisionTypeId != null) {
            params = params.set('provisionTypeId', String(provisionTypeId));
        }
        if (keyDate) {
            params = params.set('keyDate', keyDate);
        }
        if (includeGroupComparison) {
            params = params.set('includeGroupComparison', 'true');
        }

        return this.http.get<ProvisionAgentConfigurationDto>(
            this.apiUrl + `/${provisionAgentId}/provisionConfiguration`,
            {params});
    }

}
